<template>
  <div
    id="search-result-container"
    class="search-result full-width bb-px-8 bb-sm-max-p-3"
  >
    <div
      :class="[
        'items-container',
        cardSelected === card.isTravel ? 'items-container--travel' : '',
      ]"
      v-if="!isLoading"
    >
      <box-card
        v-if="cardSelected === card.isBox"
        v-for="box in filteredResults"
        :key="box.id"
        :box="box"
        class="box"
        :apply-filters="true"
        :isRetailMode="isRetailMode"
      />
      <box-card
        v-if="
          cardSelected === card.isActivity || cardSelected === card.isTravelUy
        "
        v-for="activity in filteredResults"
        :key="activity.id"
        :box="activity"
        :showPriceInResult="showPriceInResult"
        class="activity"
        :isRetailMode="isRetailMode"
      />
      <new-package-block
        v-if="cardSelected === card.isTravel"
        v-for="travelActivity in filteredResults"
        :key="travelActivity.id"
        :activity="travelActivity"
        :showPrice="true"
        class="activity-card"
      />
    </div>

    <Paginator
      v-if="pageInfo.num_pages > 1"
      :page-info="pageInfo"
      :current-page="currentPage"
      :min-pages="minPages"
      :max-pages="maxPages"
      :class="{ hidden: areResultsEmpty || isLoading }"
      @update:currentPage="updateCurrentPage"
    />

    <div
      v-if="!filteredResults.length && !isLoading"
      class="cleanFilter bb-text-center"
    >
      <p class="text-title bb-my-4">{{ emptyStateTextTitle }}</p>
      <p class="text-description bb-my-4">{{ emptyStateTextDescription }}</p>
      <button
        class="bb-button --white --home --withBorder bb-mt-4"
        @click="handleResetFilters"
      >
        Limpiar filtros
      </button>
    </div>

    <div class="bb-column full-width" v-if="isLoading">
      <skeleton-search-result />
    </div>
  </div>
</template>

<script>
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import NewBoxBlock from 'components/NewBoxBlock';
  import NewActivityBlock from 'common/components/NewActivityBlock';
  import NewPackageBlock from 'components/NewPackageBlock';
  import SkeletonSearchResult from '../SkeletonSearchResult/SkeletonSearchResult.vue';
  import BoxCard from 'common/components/BoxCard';

  import useFilters from 'composables/useFilters';
  import useIsMobile from 'common/composables/useIsMobile.js';
  import Paginator from '../Paginator/Paginator.vue';
  import useCatalogFilters from 'composables/useCatalogFilters';

  @Component({
    components: {
      NewBoxBlock,
      NewActivityBlock,
      NewPackageBlock,
      SkeletonSearchResult,
      BoxCard,
      Paginator,
    },
    props: {
      isBoxCatalog: {
        type: Boolean,
        default: false,
      },
    },
    setup(props) {
      const filterStore = props.isBoxCatalog
        ? useCatalogFilters()
        : useFilters();
      const {
        getResults,
        getCardSelected,
        getPageInfo,
        getLoading,
        getDataFromFilters,
        clearCurrentFilters,
        updatePageNumber,
      } = filterStore;

      const { isMobile, isTablet } = useIsMobile();
      return {
        results: getResults,
        cardSelected: getCardSelected,
        pageInfo: getPageInfo,
        isLoading: getLoading,
        resetFilters: clearCurrentFilters,
        getDataFromFilters,
        isMobile,
        isTablet,
        updatePageNumber,
      };
    },
  })
  export default class SearchResultComponent extends Vue {
    card = {
      isBox: 0,
      isActivity: 1,
      isTravelUy: 2,
    };

    emptyStateTextTitle = `¡Ups! lo sentimos`;
    emptyStateTextDescription = `No fue posible obtener resultados, ${this.$translate(
      'probá',
    )} cambiar o eliminar la combinacion de filtros`;

    get areResultsEmpty() {
      return this.filteredResults.length === 0;
    }

    get currentPage() {
      return this.pageInfo.number;
    }

    set currentPage(value) {
      this.smoothScrollToTop();
      this.updatePageNumber(value);
    }

    updateCurrentPage(page) {
      this.currentPage = page;
    }

    get minPages() {
      let before = this.currentPage - 1;
      if (this.currentPage === this.pageInfo.num_pages) {
        before = this.pageInfo.num_pages - 3;
      } else if (this.currentPage === this.pageInfo.num_pages - 1) {
        before = this.pageInfo.num_pages - 3;
      } else if (this.currentPage === 1) {
        before = 1;
      }
      return before;
    }

    get maxPages() {
      let after = this.currentPage + 1;
      if (this.currentPage === 1) {
        after = this.currentPage + 3;
      } else if (this.currentPage === 2) {
        after = this.currentPage + 2;
      } else if (this.currentPage === this.pageInfo.num_pages) {
        after = this.pageInfo.num_pages;
      }
      return after;
    }

    get showPriceInResult() {
      return Boolean(!this.isBoxCatalog);
    }

    get isRetailMode() {
      return this.$route.query?.retail === 'true';
    }

    get filteredResults() {
      if (this.isRetailMode) {
        // Filter out activities where partneractivity.can_purchase_a_reservation is "ACTIVE_OBLIGATORY"
        return this.results.filter(activity => {
          const canPurchaseReservation =
            activity.partneractivity?.can_purchase_a_reservation;
          return canPurchaseReservation !== 'ACTIVE_OBLIGATORY';
        });
      }
      return this.results;
    }

    smoothScrollToTop() {
      const scrollTarget = this.isBoxCatalog
        ? '.items-container'
        : this.isMobile || this.isTablet
        ? 'body'
        : '.base-header';
      document.querySelector(scrollTarget).scrollIntoView({
        behavior: 'smooth',
      });
    }

    isHasOnlineReservation(activity) {
      return activity.partneractivity?.has_online_reservation;
    }

    async handleResetFilters() {
      try {
        await this.resetFilters(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error resetting filters:', error);
      }
    }
  }
</script>

<style lang="scss">
  @import '~assets/styles/index.scss';

  .search-result {
    position: relative;

    .items-container {
      width: 100%;
      margin-bottom: 32px;
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 24px;
      justify-content: center;

      @media (max-width: $screen-xs-max) {
        gap: 16px;
        grid-template-columns: repeat(2, minmax(140px, max-content));
      }

      &--travel {
        grid-template-columns: repeat(2, 1fr);

        @media (max-width: $screen-xs-max) {
          gap: 16px;
          grid-template-columns: 1fr;
        }
      }

      .box,
      .activity {
        width: 100%;

        @media (max-width: $screen-xs-max) {
          width: 100%;
        }
      }

      .activity-card {
        width: 100%;
      }

      .cleanFilter {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;

        .text-title {
          font-size: 24px;
          font-weight: bold;
        }

        .text-description {
          font-weight: 500;
          width: 50%;

          @media screen and (max-width: $screen-sm) {
            width: 95%;
          }
        }

        button {
          width: 20%;
          font-weight: bold;

          @media screen and (max-width: $screen-sm) {
            width: 50%;
            margin-bottom: 50px;
          }
        }
      }
    }
  }
  .hidden {
    opacity: 0;
  }
</style>
